import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./motorQuoteTour.css";
import { GlobalStore } from "../globalStore";
import { appFunctions } from "./appFunctions";

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isMotorQuoteTourEnabled() {
  return String(process.env.VUE_APP_MOTOR_QUOTE_TOUR || "").toLowerCase() === "true";
}

export function shouldStartMotorQuoteTour() {
  if (!isMotorQuoteTourEnabled()) {
    return false;
  }
  const broker = GlobalStore.brokerObj;
  if (!broker) {
    return false;
  }
  if (broker.parentOrg && broker.parentOrg.is_insurance_broker === true) {
    return false;
  }
  return broker.motor_quote_tour_completed !== true;
}

export async function markMotorQuoteTourCompleted() {
  const broker = GlobalStore.brokerObj;
  if (!broker || !broker.Broker_id) {
    return;
  }
  try {
    await appFunctions.crud_broker("markMotorQuoteTourCompleted", {
      Broker_id: broker.Broker_id
    });
  } catch (e) {
    console.warn("Could not save motor quote tour flag", e);
  }
  broker.motor_quote_tour_completed = true;
}

export function startMotorQuoteTour(formVm) {
  if (!isMotorQuoteTourEnabled() || !formVm || formVm.isMotorQuoteTour) {
    return;
  }

  formVm.isMotorQuoteTour = true;
  formVm.formMode = "new";

  let finishing = false;

  const driverObj = driver({
    showProgress: true,
    allowClose: true,
    overlayClickBehavior: "none",
    disableActiveInteraction: true,
    stagePadding: 6,
    popoverClass: "motor-quote-tour",
    nextBtnText: "Next",
    doneBtnText: "Finish",
    progressText: "{{current}} of {{total}}",
    onPopoverRender: popover => {
      if (!popover.footerButtons || popover.footerButtons.querySelector(".motor-tour-skip")) {
        return;
      }
      const skipBtn = document.createElement("button");
      skipBtn.type = "button";
      skipBtn.className = "motor-tour-skip";
      skipBtn.innerText = "Skip tour";
      skipBtn.addEventListener("click", () => driverObj.destroy());
      popover.footerButtons.insertBefore(skipBtn, popover.footerButtons.firstChild);
    },
    onDestroyStarted: () => {
      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
    onDestroyed: () => {
      if (finishing) {
        return;
      }
      finishing = true;
      formVm.finishMotorQuoteTour();
    },
    steps: [
      {
        element: '[data-tour="motor-new-existing"]',
        popover: {
          title: "New or existing customer",
          description:
            "Choose New for a first-time client, or Existing to search customers you have quoted before.",
          onNextClick: () => {
            formVm.applyTourDummyCustomerType();
            formVm.$nextTick(() => driverObj.moveNext());
          }
        }
      },
      {
        element: '[data-tour="motor-business-details"]',
        popover: {
          title: "Business details",
          description:
            "Enter the business name, ABN, and business description. You can also search ABR if you do not have the ABN.",
          onNextClick: () => {
            formVm.applyTourDummyBusiness();
            formVm.$nextTick(() => driverObj.moveNext());
          }
        }
      },
      {
        element: '[data-tour="motor-contact-details"]',
        popover: {
          title: "Contact details",
          description:
            "Add the contact person, phone, email, and insured address. These are used on the quote and consent email.",
          onNextClick: () => {
            formVm.applyTourDummyContact();
            formVm.$nextTick(() => driverObj.moveNext());
          }
        }
      },
      {
        element: '[data-tour="motor-add-vehicle"]',
        popover: {
          title: "Add a vehicle",
          description: "Click Add Vehicle to open the vehicle details form. At least one vehicle is required.",
          onNextClick: async () => {
            formVm.addVehicle();
            await formVm.$nextTick();
            await wait(400);
            driverObj.moveNext();
          }
        }
      },
      {
        element: '[data-tour="motor-vehicle-details"]',
        popover: {
          title: "Vehicle details",
          description:
            "Choose the vehicle type, then enter registration or VIN, make, model, year, and sum insured.",
          onNextClick: async () => {
            formVm.applyTourDummyVehicle();
            await formVm.$nextTick();
            await wait(350);
            driverObj.moveNext();
          }
        }
      },
      {
        element: '[data-tour="motor-get-premium"]',
        popover: {
          title: "Get premium",
          description:
            "When the form is complete, Get Premium requests an indicative price. This tour uses a sample premium only.",
          onNextClick: async () => {
            formVm.applyTourMockPremium();
            await formVm.$nextTick();
            await wait(200);
            driverObj.moveNext();
          }
        }
      },
      {
        element: '[data-tour="motor-send-to-customer"]',
        popover: {
          title: "Send to customer",
          description:
            "This sends a consent email to the customer. Once they confirm, the indicative quote is emailed automatically."
        }
      }
    ]
  });

  formVm._motorQuoteTourDriver = driverObj;
  driverObj.drive();
}
